import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CollapseBlock } from '../../../../common/index.jsx';
import { useTranslation } from 'react-i18next';
import { BLOCKS_PREDEFINED } from '../../../../../utils/constants/enums.js';
import { required } from '../../../../antd/form/FormValidation.js';
import Form from '../../../../antd/form/Form/Form.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import { Alert, Form as AntdForm, Radio } from 'antd';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import LanguageBlock from './LanguageBlock.jsx';
import useFormDisabledStates from '../../../../../hooks/useFormDisabledStates.jsx';
import { AT_LEAST_ONE_LANGUAGE_VERSION_MESSAGE } from '../../../../../utils/constants/common_constants.js';
import { formIsEmpty } from '../../../../../utils/helpers/validation.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import TranslationProviderBlock from '../../../../common/TranslationProviderBlock/TranslationProviderBlock.jsx';
import QuestionModal from '../../../../common/modals/QuestionModal/QuestionModal.jsx';
import { MainButton, ButtonContainer } from '../../../../common/form/FormElements';
import {
  checkIfAltAttributesMissing,
  addAltAttributestoFormContent,
} from '../../../../../utils/helpers/imageAltValidator';

const BlockForm = ({ initials, onSubmit, positionOptions }) => {
  const { t } = useTranslation();
  const { apiErrorMessage } = useToastMessages();

  const [form] = AntdForm.useForm();
  const [showPredefined, setShowPredefined] = useState(
    Boolean(initials.generated_content && initials.generated_content > 0),
  );
  const [showAltModal, setShowAltModal] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);

  const { disabledStates, updateDisabledState } = useFormDisabledStates(
    form,
    initials,
    'label',
    'position',
    showPredefined,
  );

  const modulesOptions = [
    {
      label: t('Užitočné odkazy'),
      value: BLOCKS_PREDEFINED.BLOCK_USEFUL_LINK,
    },
    {
      label: t('Kalendár'),
      value: BLOCKS_PREDEFINED.BLOCK_CALENDAR,
    },
    {
      label: t('Počasie'),
      value: BLOCKS_PREDEFINED.BLOCK_WEATHER,
    },
  ];

  const areAllLanguageBlockEmpty = (showError) => {
    const skIsEmpty = formIsEmpty(
      form,
      'sk',
      'label',
      'position',
      showPredefined,
    );
    const enIsEmpty = formIsEmpty(
      form,
      'en',
      'label',
      'position',
      showPredefined,
    );

    // Show error message if all language blocks are empty
    if (skIsEmpty && enIsEmpty) {
      // Show error message in popup if enabled
      if (showError === true) {
        apiErrorMessage(t(AT_LEAST_ONE_LANGUAGE_VERSION_MESSAGE));
      }
      return true;
    }
    return false;
  };

  return (
    <Form
      onFinish={(values) => {
        if (areAllLanguageBlockEmpty(false)) {
          return;
        }

        const altValidationValues = {
          ...values,
          title_sk: values.label_sk,
          title_en: values.label_en,
        };

        if (checkIfAltAttributesMissing(altValidationValues)) {
          setPendingSubmitValues(values);
          setShowAltModal(true);
          return;
        }

        onSubmit(values);
      }}
      validateTrigger="onSubmit"
      initialValues={initials}
      form={form}
      scrollToFirstError={true}
    >
      <CollapseBlock collapsible collapse={false} title={t('Typ obsahu')}>
        <AntdForm.Item label={t('Typ obsahu')}>
          <Radio.Group
            value={showPredefined ? 'predefined' : 'normal'}
            onChange={(e) => {
              if (e.target.value === 'normal') {
                form.setFieldValue('generated_content', 0);
                setShowPredefined(false);
              } else {
                form.setFieldValue(
                  'generated_content',
                  BLOCKS_PREDEFINED.BLOCK_USEFUL_LINK,
                );
                setShowPredefined(true);
              }
            }}
          >
            <Radio value={'normal'}>{t('Textový obsah')}</Radio>
            <Radio value={'predefined'}>{t('Preddefinovaný modul')}</Radio>
          </Radio.Group>
        </AntdForm.Item>
        {showPredefined && (
          <FormSelect
            label={t('Modul na zobrazenie')}
            name={'generated_content'}
            options={modulesOptions}
            rules={[required(t)]}
          />
        )}
      </CollapseBlock>

      <LanguageBlock
        language="sk"
        title={t('Slovenská verzia')}
        onChange={updateDisabledState}
        positionOptions={positionOptions}
        formIsEmpty={formIsEmpty(
          form,
          'sk',
          'label',
          'position',
          showPredefined,
        )}
        formIsFilled={!disabledStates.sk}
        showPredefined={showPredefined}
        form={form}
      />

      <TranslationProviderBlock
        form={form}
        srcLang="sk"
        fields={['label', 'content']}
        targetLang="en"
      />

      <LanguageBlock
        language="en"
        title={t('Anglická verzia')}
        onChange={updateDisabledState}
        positionOptions={positionOptions}
        formIsEmpty={formIsEmpty(
          form,
          'en',
          'label',
          'position',
          showPredefined,
        )}
        formIsFilled={!disabledStates.en}
        showPredefined={showPredefined}
        form={form}
      />
      <Alert
        showIcon
        type="warning"
        title={
          <>
            <span style={{ color: 'red' }}>** </span>
            {t(AT_LEAST_ONE_LANGUAGE_VERSION_MESSAGE)}
          </>
        }
      />
      <FormButtonsContainer>
        <FormButton
          style={{ marginTop: '15px' }}
          onClick={() => areAllLanguageBlockEmpty(true)}
          submitOnClick={true}
          submitKey={'blocks'}
        >
          {t('Uložiť')}
        </FormButton>
      </FormButtonsContainer>
      <QuestionModal
        visible={showAltModal}
        toggle={() => {
          setShowAltModal(false);
          setPendingSubmitValues(null);
        }}
        headerTitle={t('Obrázkom chýbajú textové popisy')}
        question={t(
          'V obsahu bloku sú obrázky bez textových popisov (alt atribút). Chcete ich doplniť z názvu bloku?',
        )}
        showDefaultButtons={false}
        customButtons={
          <ButtonContainer>
            <MainButton
              onClick={() => {
                setShowAltModal(false);
                setPendingSubmitValues(null);
              }}
            >
              {t('Nie, doplním sám')}
            </MainButton>
            <MainButton
              onClick={() => {
                const altFillValues = {
                  ...pendingSubmitValues,
                  title_sk: pendingSubmitValues.label_sk,
                  title_en: pendingSubmitValues.label_en,
                };

                const updatedValues = addAltAttributestoFormContent(
                  altFillValues,
                  pendingSubmitValues.label_sk || '',
                  pendingSubmitValues.label_en || '',
                );

                onSubmit({
                  ...pendingSubmitValues,
                  content_sk: updatedValues.content_sk,
                  content_en: updatedValues.content_en,
                });

                setShowAltModal(false);
                setPendingSubmitValues(null);
              }}
            >
              {t('Áno, doplniť a uložiť')}
            </MainButton>
          </ButtonContainer>
        }
      />
    </Form>
  );
};

BlockForm.propTypes = {
  initials: PropTypes.object,
  positionOptions: PropTypes.array,
  onSubmit: PropTypes.func,
};

export default BlockForm;
